/**
 * Provider Selection Modal
 * Allows users to change matched providers by selecting from provider directory
 */

import { useState, useEffect } from "react";
import { X, MapPin, Search, Building, Star, Phone, Mail } from "lucide-react";
import { config } from "@/src/config";

const API_BASE_URL = config.api.baseUrl;

interface Provider {
  id: string;
  organization_name: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  phone_number?: string;
  email?: string;
  services?: string[];
  google_rating?: number;
  google_review_count?: number;
}

interface ProviderSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProvider: (provider: Provider) => void;
  memberLocation?: { latitude: number; longitude: number };
  currentProviders?: string[]; // IDs of currently selected providers
}

export function ProviderSelectionModal({
  isOpen,
  onClose,
  onSelectProvider,
  memberLocation,
  currentProviders = [],
}: ProviderSelectionModalProps) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchProviders();
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter providers based on search term
    if (searchTerm) {
      const filtered = providers.filter((p) =>
        p.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.zip_code?.includes(searchTerm)
      );
      setFilteredProviders(filtered);
    } else {
      setFilteredProviders(providers);
    }
  }, [searchTerm, providers]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/providers`);
      if (response.ok) {
        const data = await response.json();

        // Calculate distances if member location is available
        let providersWithDistance = data;
        if (memberLocation?.latitude && memberLocation?.longitude) {
          providersWithDistance = data.map((provider: Provider) => {
            if (provider.latitude && provider.longitude) {
              const distance = calculateDistance(
                memberLocation.latitude,
                memberLocation.longitude,
                provider.latitude,
                provider.longitude
              );
              return { ...provider, distance_miles: distance };
            }
            return provider;
          });

          // Sort by distance
          providersWithDistance.sort((a: any, b: any) => {
            if (a.distance_miles === undefined) return 1;
            if (b.distance_miles === undefined) return -1;
            return a.distance_miles - b.distance_miles;
          });
        }

        setProviders(providersWithDistance);
        setFilteredProviders(providersWithDistance);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c * 10) / 10;
  };

  const toRad = (degrees: number): number => {
    return degrees * (Math.PI / 180);
  };

  const handleSelectProvider = () => {
    if (selectedProviderId) {
      const provider = providers.find(p => p.id === selectedProviderId);
      if (provider) {
        onSelectProvider(provider);
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building className="w-6 h-6 text-white" />
                <h3 className="text-xl font-semibold text-white">
                  Select Provider
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, city, or zip code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Provider List */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            ) : filteredProviders.length === 0 ? (
              <div className="text-center py-8">
                <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No providers found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredProviders.map((provider) => {
                  const isCurrentlySelected = currentProviders.includes(provider.id);
                  const isSelected = selectedProviderId === provider.id;

                  return (
                    <div
                      key={provider.id}
                      onClick={() => !isCurrentlySelected && setSelectedProviderId(provider.id)}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        isCurrentlySelected
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
                          : isSelected
                          ? 'border-teal-500 bg-teal-50 shadow-md'
                          : 'border-gray-200 hover:border-teal-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold text-gray-800">
                              {provider.organization_name}
                            </h4>
                            {isCurrentlySelected && (
                              <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-md">
                                Already Selected
                              </span>
                            )}
                          </div>

                          <div className="space-y-1 text-sm text-gray-600">
                            {provider.street_address && (
                              <div className="flex items-center space-x-2">
                                <MapPin className="w-3 h-3" />
                                <span>
                                  {provider.street_address}, {provider.city}, {provider.state} {provider.zip_code}
                                </span>
                              </div>
                            )}

                            {provider.phone_number && (
                              <div className="flex items-center space-x-2">
                                <Phone className="w-3 h-3" />
                                <span>{provider.phone_number}</span>
                              </div>
                            )}

                            {provider.google_rating && (
                              <div className="flex items-center space-x-2">
                                <Star className="w-3 h-3 text-yellow-500" />
                                <span>
                                  {provider.google_rating.toFixed(1)} ({provider.google_review_count} reviews)
                                </span>
                              </div>
                            )}
                          </div>

                          {provider.services && provider.services.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {provider.services.slice(0, 3).map((service, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md"
                                >
                                  {service}
                                </span>
                              ))}
                              {provider.services.length > 3 && (
                                <span className="px-2 py-1 text-gray-500 text-xs">
                                  +{provider.services.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {(provider as any).distance_miles !== undefined && (
                          <div className="ml-4 text-right">
                            <div className="text-2xl font-bold text-teal-600">
                              {(provider as any).distance_miles.toFixed(1)}
                            </div>
                            <div className="text-xs text-gray-500">miles away</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelectProvider}
              disabled={!selectedProviderId}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Select Provider
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
